import { useMemo, useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Container, Row, Col, Tab, Nav } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import EventVariableList from "components/events/EventVariable/EventVariableList";
import EventList from "components/events/EventList/EventList";
import useCollectSort from "hooks/useCollectSort";
import {
    EVENT_ACTIVE_KEY,
    FETCH_EVENTS_FAIL_MESSAGE,
    FETCH_EVENT_VARIABLES_FAIL_MESSAGE,
    FETCH_GATEWAY_FAIL_MESSAGE,
    HttpStatus,
} from "constant";
import { canAccess } from "utils/authorize-action";
import { isHttpSuccess } from "utils/functions";
import { getAPIError, showErrorAlert } from "utils/alert";
import EventService, { EventBy } from "service/eventService";
import { getAllRegistry } from "service/gatewayService";
import EventVariableService from "service/eventVariableService";
import { orderBy, get, uniqBy } from "lodash";
import { Variable } from "generated/models/Event";
import { Event } from "types/Event";
import { Registry } from "generated/models";
import { useAppContext } from "context/appContext";

const EVENT_TABS = {
    events: "all-events",
    eventVariables: "event-variables",
} as const;

export type PaginationType = {
    page: number;
    rowsPerPage: number;
    count: number;
    lastPage: number;
    loading: boolean;
};

type EventState = {
    list: Event[];
    variables: Variable[];
    allGateways: Registry[];
    associatedEventMap: Record<string, number>;
    searchText: string;
    pagination: PaginationType;
};

const INITIAL_PAGINATION: PaginationType = {
    page: 0,
    rowsPerPage: 10,
    count: 0,
    lastPage: 0,
    loading: true,
};

/**
 * Calculate the number of associated events for each variable
 */
const calculateAssociatedEventMap = (
    variables: Variable[]
): Record<string, number> => {
    const eventVariableMap: Record<string, number> = {};
    variables.forEach((variable: Variable) => {
        const in_actions = get(variable, "in_actions", []);
        const in_conditions = get(variable, "in_conditions", []);
        eventVariableMap[variable.uuid] = uniqBy(
            [...in_actions, ...in_conditions],
            "uuid"
        ).length;
    });
    return eventVariableMap;
};

/**
 * Custom hook to manage active tab from URL query params
 */
const useActiveTab = (defaultTab: string) => {
    const { search } = useLocation();
    const history = useHistory();

    const activeTab = useMemo(() => {
        const queryParams = new URLSearchParams(search);
        return queryParams.get(EVENT_ACTIVE_KEY) || defaultTab;
    }, [search, defaultTab]);

    const setActiveTab = useCallback(
        (tabKey: string) => {
            const queryParams = new URLSearchParams(search);
            queryParams.set(EVENT_ACTIVE_KEY, tabKey);
            history.replace({
                search: queryParams.toString(),
            });
        },
        [search, history]
    );

    return [activeTab, setActiveTab] as const;
};

const Events = () => {
    const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [isReady, setIsReady] = useState<boolean>(false);

    const [eventState, setEventState] = useState<EventState>({
        list: [],
        variables: [],
        allGateways: [],
        associatedEventMap: {},
        searchText: "",
        pagination: INITIAL_PAGINATION,
    });

    const [activeTab, setActiveTab] = useActiveTab(EVENT_TABS.events);

    const {
        key: sortKey,
        direction: sortDirection,
        sortIcon,
    } = useCollectSort();

    const {
        storeData: {
            organization: { currentOrgId },
            systemPermissions: { permissions },
        },
    } = useAppContext();

    const hasPermission = useMemo(
        () => canAccess("event:read") && !isForbiddenResource,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [permissions, isForbiddenResource]
    );

    /**
     * Fetch event variables and calculate associated event map
     */
    const fetchEventVariables = useCallback(async () => {
        const fetchResp = await EventVariableService.readEventVariables();

        if (!isHttpSuccess(fetchResp.status)) {
            showErrorAlert(
                getAPIError(fetchResp, FETCH_EVENT_VARIABLES_FAIL_MESSAGE)
            );
            return;
        }

        const variables = orderBy(fetchResp.data, ["create_time"], ["asc"]);
        const associatedEventMap = calculateAssociatedEventMap(fetchResp.data);
        setEventState((prev) => ({
            ...prev,
            variables,
            associatedEventMap,
        }));
    }, []);

    /**
     * Fetch event list with pagination and search
     */
    const fetchEventList = useCallback(async () => {
        const page = eventState.pagination.page;
        const rowsPerPage = eventState.pagination.rowsPerPage;
        const lastPage = eventState.pagination.lastPage;
        const searchText = eventState.searchText;

        // Adjust page if last item was deleted
        let adjustedPage = page;
        if (
            eventState.list.length === 1 &&
            page === lastPage &&
            lastPage !== 0
        ) {
            adjustedPage = page - 1;
        }

        const fetchResp = await EventService.readEvent({
            offset: searchText ? 0 : adjustedPage * rowsPerPage,
            limit: rowsPerPage,
            by: (sortKey || "create_time") as EventBy,
            order: sortDirection,
            ...(searchText && {
                filter: "name",
                name: searchText.toLowerCase(),
            }),
        });

        if (fetchResp.status === HttpStatus.FORBIDDEN) {
            setIsForbiddenResource(true);
            return;
        }

        if (!isHttpSuccess(fetchResp.status)) {
            showErrorAlert(getAPIError(fetchResp, FETCH_EVENTS_FAIL_MESSAGE));
            return;
        }

        const totalEvent = get(fetchResp, "total", 0);
        setEventState((prev) => ({
            ...prev,
            list: fetchResp.data,
            pagination: {
                ...prev.pagination,
                page: searchText ? 0 : adjustedPage,
                count: totalEvent,
                lastPage: Math.ceil(totalEvent / rowsPerPage) - 1,
                loading: false,
            },
        }));
    }, [
        eventState.pagination.page,
        eventState.pagination.rowsPerPage,
        eventState.pagination.lastPage,
        eventState.list.length,
        eventState.searchText,
        sortKey,
        sortDirection,
    ]);

    /**
     * Fetch all gateways
     */
    const fetchAllGateways = useCallback(async () => {
        const fetchResp = await getAllRegistry();
        if (!isHttpSuccess(fetchResp.status)) {
            showErrorAlert(getAPIError(fetchResp, FETCH_GATEWAY_FAIL_MESSAGE));
            return;
        }
        setEventState((prev) => ({
            ...prev,
            allGateways: get(fetchResp, "data", []),
        }));
    }, []);

    // Permission check
    useEffect(() => {
        if (hasPermission) {
            setIsForbiddenResource(false);
            return;
        }

        setIsPageLoading(false);
        setIsForbiddenResource(true);
    }, [hasPermission]);

    // Initial data fetch
    useEffect(() => {
        if (!hasPermission || !currentOrgId) return;
        const fetchInitialData = async () => {
            setIsPageLoading(true);
            await fetchEventList();
            setIsPageLoading(false);
            setIsReady(true);
        };

        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission, currentOrgId]); // Note: fetchEventList excluded to avoid infinite loop

    // Fetch event list when dependencies change
    useEffect(() => {
        if (!hasPermission || !isReady) return;

        fetchEventList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasPermission,
        isReady,
        eventState.pagination.page,
        eventState.pagination.rowsPerPage,
        eventState.searchText,
        sortKey,
        sortDirection,
        currentOrgId, // Note: fetchEventList excluded to avoid infinite loop
    ]);

    // Fetch event variables and gateways after initial load
    useEffect(() => {
        if (!hasPermission || !isReady) return;

        const fetchSecondaryData = async () => {
            await Promise.all([fetchEventVariables(), fetchAllGateways()]);
        };

        fetchSecondaryData();
    }, [hasPermission, isReady, fetchEventVariables, fetchAllGateways]);

    const handleSelectActiveTab = (tabKey: string | null) => {
        setActiveTab(tabKey ?? EVENT_TABS.events);
    };

    const updateEventSearchText = useCallback((text: string) => {
        setEventState((prev) => ({ ...prev, searchText: text }));
    }, []);

    const updatePagination = useCallback(
        (pagination: Partial<PaginationType>) => {
            setEventState((prev) => ({
                ...prev,
                pagination: { ...prev.pagination, ...pagination },
            }));
        },
        []
    );

    const updateEventList = useCallback((list: Event[]) => {
        setEventState((prev) => ({ ...prev, list }));
    }, []);

    return (
        <ContentWrapper
            isForbiddenResource={isForbiddenResource}
            isLoading={isPageLoading}
            title="Event Management"
        >
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={12}>
                            <h5 className="page-title">Event Management</h5>
                        </Col>
                    </Row>

                    <Row className="mt-3">
                        <Col>
                            <div className="page-tabs">
                                <Tab.Container
                                    defaultActiveKey={activeTab}
                                    activeKey={activeTab}
                                    onSelect={handleSelectActiveTab}
                                >
                                    <Row>
                                        <Col sm={12}>
                                            <Nav>
                                                <Nav.Item>
                                                    <Nav.Link
                                                        eventKey={
                                                            EVENT_TABS.events
                                                        }
                                                    >
                                                        <h6>
                                                            All Events{" "}
                                                            <span className="count">
                                                                {
                                                                    eventState
                                                                        .list
                                                                        .length
                                                                }
                                                            </span>
                                                        </h6>
                                                    </Nav.Link>
                                                </Nav.Item>

                                                <Nav.Item>
                                                    <Nav.Link
                                                        eventKey={
                                                            EVENT_TABS.eventVariables
                                                        }
                                                    >
                                                        <h6>
                                                            Event Variables{" "}
                                                            <span className="count">
                                                                {
                                                                    eventState
                                                                        .variables
                                                                        .length
                                                                }
                                                            </span>
                                                        </h6>
                                                    </Nav.Link>
                                                </Nav.Item>
                                            </Nav>
                                        </Col>

                                        <Col sm={12}>
                                            <Tab.Content className="mt-4">
                                                <Tab.Pane
                                                    eventKey={EVENT_TABS.events}
                                                >
                                                    <EventList
                                                        eventList={
                                                            eventState.list
                                                        }
                                                        allGateways={
                                                            eventState.allGateways
                                                        }
                                                        fetchEventList={
                                                            fetchEventList
                                                        }
                                                        setEventList={
                                                            updateEventList
                                                        }
                                                        eventSearchText={
                                                            eventState.searchText
                                                        }
                                                        setEventSearchText={
                                                            updateEventSearchText
                                                        }
                                                        pagination={
                                                            eventState.pagination
                                                        }
                                                        setPagination={
                                                            updatePagination
                                                        }
                                                        sortIcon={sortIcon}
                                                    />
                                                </Tab.Pane>
                                                <Tab.Pane
                                                    eventKey={
                                                        EVENT_TABS.eventVariables
                                                    }
                                                >
                                                    <EventVariableList
                                                        associatedEventMap={
                                                            eventState.associatedEventMap
                                                        }
                                                        eventVariables={
                                                            eventState.variables
                                                        }
                                                        fetchEventVariables={
                                                            fetchEventVariables
                                                        }
                                                    />
                                                </Tab.Pane>
                                            </Tab.Content>
                                        </Col>
                                    </Row>
                                </Tab.Container>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default Events;
